'use client';

import { useRef } from 'react';
import { AppExperienceContentSchema, type TAppExperienceContent } from '@shared/schemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Smartphone, Building2, Upload, Trash2, Check, ExternalLink, Globe } from 'lucide-react';

type FormValue = Partial<TAppExperienceContent>;

interface Props {
	value: FormValue;
	onChange: (value: FormValue) => void;
	onValidityChange: (valid: boolean) => void;
}

const COLOR_PALETTES = [
	{ name: 'App Store Blue', hex: '#2563eb' },
	{ name: 'Emerald Green', hex: '#059669' },
	{ name: 'Indigo Purple', hex: '#6366f1' },
	{ name: 'Ruby Red', hex: '#e11d48' },
	{ name: 'Charcoal Dark', hex: '#0f172a' },
	{ name: 'Amber Gold', hex: '#d97706' },
];

export function AppExperienceForm({ value, onChange, onValidityChange }: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const updateFields = (patch: Partial<TAppExperienceContent>) => {
		const updated = {
			type: 'app' as const,
			appName: value.appName ?? '',
			accentColor: value.accentColor ?? '#2563eb',
			...value,
			...patch,
		};
		onChange(updated);
		onValidityChange(AppExperienceContentSchema.safeParse(updated).success);
	};

	const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 1_000_000) {
			alert('App icon must be under 1 MB.');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				updateFields({ logoUrl: reader.result });
			}
		};
		reader.readAsDataURL(file);
	};

	const testUrl = (targetUrl?: string) => {
		if (targetUrl && (targetUrl.startsWith('http://') || targetUrl.startsWith('https://'))) {
			window.open(targetUrl, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold tracking-tight text-slate-950">App Details</h1>
				<p className="mt-1 text-sm text-slate-600">
					Direct your audience to download your mobile application on iOS and Android.
				</p>
			</div>

			{/* App Identity & Branding */}
			<div className="rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6 space-y-4 shadow-2xs">
				<div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
					<Smartphone className="size-5" />
					<span>App Information</span>
				</div>

				<div className="space-y-2">
					<Label htmlFor="appName" className="text-xs font-semibold text-slate-700">
						App Name <span className="text-rose-500">*</span>
					</Label>
					<Input
						id="appName"
						placeholder="e.g. Ekutu QR Scanner & Maker"
						value={value.appName ?? ''}
						onChange={(e) => updateFields({ appName: e.target.value })}
						maxLength={120}
						className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="appDeveloper" className="text-xs font-semibold text-slate-700">
						Developer or Company Name
					</Label>
					<Input
						id="appDeveloper"
						placeholder="e.g. Ekutu Limited"
						value={value.developer ?? ''}
						onChange={(e) => updateFields({ developer: e.target.value })}
						maxLength={120}
						className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="appDescription" className="text-xs font-semibold text-slate-700">
						Description / Key Features
					</Label>
					<Textarea
						id="appDescription"
						placeholder="Highlight what your app does, key benefits, or update notes..."
						value={value.description ?? ''}
						onChange={(e) => updateFields({ description: e.target.value })}
						maxLength={1500}
						rows={3}
						className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none text-sm"
					/>
				</div>

				{/* App Icon / Logo */}
				<div className="pt-2">
					<Label className="text-xs font-semibold text-slate-700 block mb-2">App Icon / Logo</Label>
					<div className="flex items-center gap-4">
						{value.logoUrl ? (
							<div className="relative group size-20 rounded-2xl overflow-hidden border border-slate-200 shadow-xs bg-slate-50 flex items-center justify-center">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img src={value.logoUrl} alt="App Icon" className="size-full object-cover" />
								<button
									type="button"
									onClick={() => updateFields({ logoUrl: undefined })}
									className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer"
								>
									<Trash2 className="size-4" />
								</button>
							</div>
						) : (
							<div
								onClick={() => fileInputRef.current?.click()}
								className="size-20 rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 bg-slate-50/50 flex flex-col items-center justify-center gap-1 cursor-pointer transition text-slate-400 hover:text-slate-600"
							>
								<Upload className="size-5" />
								<span className="text-[10px] font-medium">Upload Icon</span>
							</div>
						)}
						<input
							type="file"
							ref={fileInputRef}
							accept="image/png,image/jpeg,image/webp,image/svg+xml"
							className="hidden"
							onChange={handleLogoUpload}
						/>
						<p className="text-xs text-slate-500 max-w-xs leading-relaxed">
							Upload a square app icon (PNG or JPEG, up to 1 MB). Recommended 512×512px.
						</p>
					</div>
				</div>

				{/* Theme Accent Color */}
				<div className="pt-2">
					<Label className="text-xs font-semibold text-slate-700 block mb-2">
						Theme Accent Color
					</Label>
					<div className="flex flex-wrap items-center gap-2">
						{COLOR_PALETTES.map((c) => {
							const selected = (value.accentColor ?? '#2563eb') === c.hex;
							return (
								<button
									key={c.hex}
									type="button"
									onClick={() => updateFields({ accentColor: c.hex })}
									className="relative size-8 rounded-full transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 cursor-pointer"
									style={{ backgroundColor: c.hex }}
									title={c.name}
								>
									{selected && (
										<Check className="absolute inset-0 m-auto size-4 text-white stroke-[3]" />
									)}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			{/* Store Download Links */}
			<div className="rounded-2xl border border-slate-200/80 bg-white p-5 sm:p-6 space-y-4 shadow-2xs">
				<div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
					<Building2 className="size-5" />
					<span>Download Links & Stores</span>
				</div>
				<p className="text-xs text-slate-500">
					Add at least one store URL. When customers scan on an iPhone or Android phone, the page
					will present the appropriate store link.
				</p>

				{/* Apple App Store */}
				<div className="space-y-1.5">
					<Label htmlFor="appIosUrl" className="text-xs font-semibold text-slate-700">
						Apple App Store URL (iOS)
					</Label>
					<div className="flex gap-2">
						<Input
							id="appIosUrl"
							type="url"
							placeholder="https://apps.apple.com/app/id123456789"
							value={value.iosUrl ?? ''}
							onChange={(e) => updateFields({ iosUrl: e.target.value })}
							className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 font-mono text-xs"
						/>
						{value.iosUrl && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => testUrl(value.iosUrl)}
								className="rounded-xl text-xs shrink-0"
							>
								<ExternalLink className="mr-1.5 size-3.5" />
								Test
							</Button>
						)}
					</div>
				</div>

				{/* Google Play Store */}
				<div className="space-y-1.5">
					<Label htmlFor="appAndroidUrl" className="text-xs font-semibold text-slate-700">
						Google Play Store URL (Android)
					</Label>
					<div className="flex gap-2">
						<Input
							id="appAndroidUrl"
							type="url"
							placeholder="https://play.google.com/store/apps/details?id=com.example.app"
							value={value.androidUrl ?? ''}
							onChange={(e) => updateFields({ androidUrl: e.target.value })}
							className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 font-mono text-xs"
						/>
						{value.androidUrl && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => testUrl(value.androidUrl)}
								className="rounded-xl text-xs shrink-0"
							>
								<ExternalLink className="mr-1.5 size-3.5" />
								Test
							</Button>
						)}
					</div>
				</div>

				{/* Fallback Website */}
				<div className="space-y-1.5 pt-1">
					<Label
						htmlFor="appWebsiteUrl"
						className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"
					>
						<Globe className="size-3.5" />
						<span>Official Website / Web App URL (Optional)</span>
					</Label>
					<div className="flex gap-2">
						<Input
							id="appWebsiteUrl"
							type="url"
							placeholder="https://yourapp.com"
							value={value.websiteUrl ?? ''}
							onChange={(e) => updateFields({ websiteUrl: e.target.value })}
							className="rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 font-mono text-xs"
						/>
						{value.websiteUrl && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => testUrl(value.websiteUrl)}
								className="rounded-xl text-xs shrink-0"
							>
								<ExternalLink className="mr-1.5 size-3.5" />
								Test
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
